import React, { Component } from "react";
import clsx from "clsx";
import moment from "moment";

import { I } from "./common/icons";
import { SERVICES, SERVICE_ORDER, STATUSES, FREQUENCIES, CADENCES } from "./backofficeConstants";
import { DATE, today, fmt, fmtLong, isOverdue, isComplete, budgetTotal, loggedTotal, hrs, userName } from "./backofficeUtils";

const DN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MINI_DN = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Month / week / day calendar of task due dates (optionally start dates) and scheduled email
 * sends, with client and service filters and a popover for each event.
 */
class CalendarView extends Component {
	constructor(props) {
		super(props);
		this.state = {
			mode: "month",
			calDate: today(),
			selDate: today(),
			showEmail: true,
			hideDone: false,
			showStart: false,
			calClients: new Set(props.clients.map(c => c.clientId)),
			calSvcs: new Set(SERVICE_ORDER),
			pop: null,
		};
	}

	componentDidMount() {
		document.addEventListener("mousedown", this.onDocClick);
		if (this.props.focusClientId) this.setState({ calClients: new Set([this.props.focusClientId]) });
	}

	componentWillUnmount() {
		document.removeEventListener("mousedown", this.onDocClick);
	}

	componentDidUpdate(prev) {
		if (prev.clients !== this.props.clients) {
			const known = new Set(this.props.clients.map(c => c.clientId));
			const calClients = new Set([...this.state.calClients].filter(id => known.has(id)));
			this.props.clients.forEach(c => {
				if (!prev.clients.some(p => p.clientId === c.clientId)) calClients.add(c.clientId);
			});
			this.setState({ calClients });
		}
	}

	onDocClick = e => {
		if (this.state.pop && !e.target.closest(".bo-popover") && !e.target.closest(".bo-ev")) this.setState({ pop: null });
	};

	// ------------------------------------------------------------------ events

	events() {
		const { tasks, clients, serviceFilter, passFilters } = this.props;
		const { calClients, calSvcs, showEmail, hideDone, showStart } = this.state;
		const clientById = {};
		clients.forEach(c => (clientById[c.clientId] = c));
		const out = [];
		tasks.forEach(t => {
			const c = clientById[t.clientId];
			if (!c) return;
			if (!calClients.has(t.clientId) || !calSvcs.has(t.service)) return;
			if (serviceFilter && t.service !== serviceFilter) return;
			if (!passFilters(t)) return;
			const done = isComplete(t);
			if (hideDone && done) return;
			out.push({ kind: "task", date: t.dueDate, c, t, label: t.title });
			if (showStart && t.startDate !== t.dueDate) out.push({ kind: "start", date: t.startDate, c, t, label: "Start · " + t.title });
			if (showEmail && t.emailAutomation && t.emailAutomation.markOnCalendar && !done) {
				t.emailAutomation.sends
					.filter(s => s.status === "scheduled" || s.status === "sent")
					.forEach((s, i) => out.push({ kind: "email", date: s.scheduledFor, c, t, send: s, time: (s.scheduledTime || "09:00").slice(0, 5), label: (s.sequence === 0 ? "Email · " : "Follow-up · ") + t.emailAutomation.subject }));
			}
		});
		return out.sort((a, b) => (a.kind === "email") - (b.kind === "email"));
	}

	byDate(evs) {
		const map = {};
		evs.forEach(e => (map[e.date] = map[e.date] || []).push(e));
		return map;
	}

	// ------------------------------------------------------------------ navigation

	shift = dir => {
		const { mode, calDate } = this.state;
		const d = moment(calDate, DATE);
		if (mode === "month") d.add(dir, "months");
		else d.add(dir * (mode === "week" ? 7 : 1), "days");
		this.setState({ calDate: d.format(DATE), pop: null });
	};

	shiftMini = dir => {
		this.setState({ calDate: moment(this.state.calDate, DATE).add(dir, "months").format(DATE) });
	};

	setMode = mode => this.setState({ mode, pop: null });

	openPop = (e, anchor) => {
		const r = anchor.getBoundingClientRect();
		let left = r.right + 8;
		let top = r.top;
		if (left + 340 > window.innerWidth) left = Math.max(16, r.left - 348);
		if (top + 280 > window.innerHeight) top = Math.max(16, window.innerHeight - 290);
		this.setState({ pop: { ev: e, left, top } });
	};

	// ------------------------------------------------------------------ render pieces

	renderEvent(e, key) {
		const cls = e.kind === "email" ? "bo-ev--email" : `bo-ev--${SERVICES[e.t.service].cls}`;
		const done = e.kind !== "email" && isComplete(e.t);
		const short = (e.c.name || "").split(" ")[0];
		return (
			<div
				key={key}
				className={clsx("bo-ev", cls, { "bo-ev--done": done })}
				title={`${e.c.name} · ${e.label}`}
				onClick={ev => {
					ev.stopPropagation();
					this.openPop(e, ev.currentTarget);
				}}
			>
				{e.kind === "email" ? <I.mail /> : e.t.frequency !== "one_time" ? <I.repeat /> : null}
				{e.time && <span className="bo-ev__t">{e.time}</span>}
				<span>
					{short} · {e.label}
				</span>
			</div>
		);
	}

	renderMonth(byDate) {
		const { calDate, selDate } = this.state;
		const d = moment(calDate, DATE);
		const first = d.clone().startOf("month");
		const start = first.clone().subtract(first.day(), "days");
		const cells = [];
		for (let i = 0; i < 42; i++) {
			const cur = start.clone().add(i, "days");
			const k = cur.format(DATE);
			const list = byDate[k] || [];
			const max = 4;
			cells.push(
				<div
					key={k}
					className={clsx("bo-day", { "bo-day--other": cur.month() !== d.month(), "bo-day--today": k === today(), "bo-day--sel": k === selDate })}
					onClick={() => {
						this.setState({ selDate: k, pop: null });
						this.props.onCreate(k);
					}}
				>
					<span className="bo-dn">{cur.date()}</span>
					{list.slice(0, max).map((e, idx) => this.renderEvent(e, `${k}-${idx}`))}
					{list.length > max && (
						<span
							className="bo-more"
							onClick={ev => {
								ev.stopPropagation();
								this.setState({ calDate: k, selDate: k, mode: "day", pop: null });
							}}
						>
							+{list.length - max} more
						</span>
					)}
				</div>,
			);
		}
		return (
			<>
				<div className="bo-dow-row">
					{DN.map(x => (
						<div key={x}>{x}</div>
					))}
				</div>
				<div className="bo-month-grid">{cells}</div>
			</>
		);
	}

	renderWeekOrDay(byDate) {
		const { calDate, mode } = this.state;
		const cols = mode === "week" ? 7 : 1;
		const d = moment(calDate, DATE);
		const start = cols === 7 ? d.clone().subtract(d.day(), "days") : d.clone();
		const days = [];
		for (let i = 0; i < cols; i++) days.push(start.clone().add(i, "days").format(DATE));
		const hours = [];
		for (let h = 8; h <= 17; h++) hours.push(h);
		return (
			<div className="bo-week-wrap">
				<div className="bo-week" style={{ "--cols": cols }}>
					<div className="bo-week__hd" />
					{days.map(k => {
						const x = moment(k, DATE);
						return (
							<div key={k} className={clsx("bo-week__hd", { "bo-week__hd--today": k === today() })}>
								{DN[x.day()]}
								<b>{x.date()}</b>
							</div>
						);
					})}
					<div className="bo-week__gut">all-day</div>
					{days.map(k => (
						<div key={`a-${k}`} className="bo-week__allday" onClick={() => this.props.onCreate(k)}>
							{(byDate[k] || []).filter(e => e.kind !== "email").map((e, idx) => this.renderEvent(e, `${k}-a-${idx}`))}
						</div>
					))}
					{hours.map(h => (
						<React.Fragment key={h}>
							<div className="bo-week__gut">{(h % 12 || 12) + (h < 12 ? " AM" : " PM")}</div>
							{days.map(k => (
								<div key={`${k}-${h}`} className="bo-week__hour" onClick={() => this.props.onCreate(k)}>
									{(byDate[k] || [])
										.filter(e => e.kind === "email" && parseInt(e.time.split(":")[0], 10) === h)
										.map((e, idx) => this.renderEvent(e, `${k}-${h}-${idx}`))}
								</div>
							))}
						</React.Fragment>
					))}
				</div>
			</div>
		);
	}

	renderMini(byDate) {
		const { calDate, selDate } = this.state;
		const d = moment(calDate, DATE);
		const first = d.clone().startOf("month");
		const start = first.clone().subtract(first.day(), "days");
		const cells = [];
		for (let i = 0; i < 42; i++) {
			const cur = start.clone().add(i, "days");
			const k = cur.format(DATE);
			cells.push(
				<button
					key={k}
					type="button"
					className={clsx("bo-mini__d", { "bo-mini__d--other": cur.month() !== d.month(), "bo-mini__d--today": k === today(), "bo-mini__d--sel": k === selDate, "bo-mini__d--has": (byDate[k] || []).length })}
					onClick={() => this.setState({ selDate: k, calDate: k, pop: null })}
				>
					{cur.date()}
				</button>,
			);
		}
		return (
			<div className="bo-mini">
				<div className="bo-mini__head">
					<span>{d.format("MMMM YYYY")}</span>
					<span className="bo-mini__nb">
						<button className="bo-ico" type="button" aria-label="Previous month" onClick={() => this.shiftMini(-1)}>
							<I.chevL />
						</button>
						<button className="bo-ico" type="button" aria-label="Next month" onClick={() => this.shiftMini(1)}>
							<I.chevR />
						</button>
					</span>
				</div>
				<div className="bo-mini__grid">
					{MINI_DN.map((x, i) => (
						<div key={i} className="bo-mini__dow">
							{x}
						</div>
					))}
					{cells}
				</div>
			</div>
		);
	}

	renderSide(byDate) {
		const { clients, tasks } = this.props;
		const { calClients, calSvcs, showEmail, hideDone, showStart, selDate } = this.state;
		const toggleSet = (set, key, on) => {
			const next = new Set(set);
			on ? next.add(key) : next.delete(key);
			return next;
		};
		return (
			<aside className="bo-cal__side">
				<button className="bo-create-btn" type="button" onClick={() => this.props.onCreate(selDate)}>
					<span className="bo-create-btn__plus">
						<I.plusPlain />
					</span>
					Create
				</button>
				{this.renderMini(byDate)}
				<div className="bo-side-sec">
					<h4>Clients</h4>
					{clients.map(c => (
						<label key={c.id} className="bo-chk">
							<input type="checkbox" checked={calClients.has(c.clientId)} onChange={e => this.setState({ calClients: toggleSet(calClients, c.clientId, e.target.checked) })} />
							<span>{c.name}</span>
							<span className="bo-chk__cnt">{tasks.filter(t => t.clientId === c.clientId).length}</span>
						</label>
					))}
				</div>
				<div className="bo-side-sec">
					<h4>Services</h4>
					{SERVICE_ORDER.map(s => (
						<label key={s} className="bo-chk">
							<input type="checkbox" checked={calSvcs.has(s)} onChange={e => this.setState({ calSvcs: toggleSet(calSvcs, s, e.target.checked) })} />
							<span className="bo-chk__sw" style={{ background: SERVICES[s].color }} />
							<span>{SERVICES[s].label}</span>
						</label>
					))}
					<label className="bo-chk">
						<input type="checkbox" checked={showEmail} onChange={e => this.setState({ showEmail: e.target.checked })} />
						<span className="bo-chk__sw bo-chk__sw--email" />
						<span>Recurring email sends</span>
					</label>
				</div>
				<div className="bo-side-sec">
					<h4>Display</h4>
					<div className="bo-switch">
						<span>Show recurring email sends</span>
						<button className={clsx("bo-tog", { "bo-tog--on": showEmail })} type="button" aria-label="Toggle email sends" onClick={() => this.setState({ showEmail: !showEmail })} />
					</div>
					<div className="bo-switch">
						<span>Hide completed tasks</span>
						<button className={clsx("bo-tog", { "bo-tog--on": hideDone })} type="button" aria-label="Toggle completed tasks" onClick={() => this.setState({ hideDone: !hideDone })} />
					</div>
					<div className="bo-switch">
						<span>Show start dates too</span>
						<button className={clsx("bo-tog", { "bo-tog--on": showStart })} type="button" aria-label="Toggle start dates" onClick={() => this.setState({ showStart: !showStart })} />
					</div>
				</div>
			</aside>
		);
	}

	renderPopover() {
		const { pop } = this.state;
		if (!pop) return null;
		const { users, gmailAddress, onToggleComplete, onEdit, onOpenEmail, onSkipSend, onGotoClient } = this.props;
		const e = pop.ev;
		const t = e.t;
		const S = SERVICES[t.service];
		const color = e.kind === "email" ? "var(--bo-svc-email)" : S.color;
		const close = () => this.setState({ pop: null });
		return (
			<div className="bo-popover" style={{ left: pop.left, top: pop.top }}>
				<div className="bo-popover__head" style={{ borderLeftColor: color }}>
					<div>
						<b>{e.kind === "email" ? t.emailAutomation.subject : t.title}</b>
						<span>
							{e.c.name} · {S.label}
							{e.kind === "email" ? " · Automated email" : ""}
						</span>
					</div>
				</div>
				{e.kind === "email" ? (
					<>
						<div className="bo-popover__body">
							<div className="bo-kv">
								<I.clock />
								{fmtLong(e.date)} · {e.time} · {e.send.sequence === 0 ? "First send" : e.send.sequence < 0 ? "Sent manually" : `Follow-up ${e.send.sequence}`}
								{e.send.status === "sent" ? " · sent" : ""}
							</div>
							<div className="bo-kv">
								<I.user />
								To {t.emailAutomation.recipientEmail}
								{gmailAddress ? ` · from ${gmailAddress}` : ""}
							</div>
							<div className="bo-kv">
								<I.repeat />
								{CADENCES[t.emailAutomation.cadence]}
							</div>
							{t.emailAutomation.body && <div className="bo-kv bo-kv--quote">“{t.emailAutomation.body}”</div>}
						</div>
						<div className="bo-popover__foot">
							<button
								className="bo-btn bo-btn--sm bo-btn--email"
								type="button"
								onClick={() => {
									close();
									onOpenEmail(t);
								}}
							>
								<I.mail />
								Edit automation
							</button>
							{e.send.status === "scheduled" && (
								<button
									className="bo-btn bo-btn--sm"
									type="button"
									onClick={() => {
										close();
										onSkipSend(t, e.send);
									}}
								>
									Skip this send
								</button>
							)}
						</div>
					</>
				) : (
					<>
						<div className="bo-popover__body">
							<div className="bo-kv">
								<I.cal />
								Start {fmt(t.startDate)} · Due {fmt(t.dueDate)} · {FREQUENCIES[t.frequency]}
							</div>
							<div className="bo-kv">
								<I.user />
								P: {userName(users, t.preparerUserId) || "—"} · R: {userName(users, t.reviewerUserId) || "—"} · M: {userName(users, t.managerUserId) || "—"}
							</div>
							<div className="bo-kv">
								<span className={clsx("bo-status", "bo-status--static", `bo-status--${STATUSES[t.status].cls}`)}>{STATUSES[t.status].label}</span>
								{isOverdue(t) && <span className="bo-pill bo-pill--red">Overdue</span>}
							</div>
							{budgetTotal(t) > 0 && (
								<div className="bo-kv">
									<I.clock />
									Budget {hrs(budgetTotal(t))}h · logged {hrs(loggedTotal(t))}h
								</div>
							)}
							{t.accounts.length > 0 && (
								<div className="bo-kv">
									<I.card />
									{t.accounts.filter(a => a.isDone).length} / {t.accounts.length} accounts done
								</div>
							)}
							{t.hint && <div className="bo-kv">{t.hint}</div>}
						</div>
						<div className="bo-popover__foot">
							<button
								className="bo-btn bo-btn--sm bo-btn--primary"
								type="button"
								onClick={() => {
									close();
									onToggleComplete(t);
								}}
							>
								{isComplete(t) ? "Mark incomplete" : "Mark complete"}
							</button>
							<button
								className="bo-btn bo-btn--sm"
								type="button"
								onClick={() => {
									close();
									onEdit(t);
								}}
							>
								<I.edit />
								Edit
							</button>
							<button
								className="bo-btn bo-btn--sm"
								type="button"
								onClick={() => {
									close();
									onGotoClient(t.clientId, t.service);
								}}
							>
								Open client
							</button>
						</div>
					</>
				)}
			</div>
		);
	}

	render() {
		const { mode, calDate } = this.state;
		const evs = this.events();
		const byDate = this.byDate(evs);
		const d = moment(calDate, DATE);
		let title;
		if (mode === "month") title = d.format("MMMM YYYY");
		else if (mode === "week") {
			const s = d.clone().subtract(d.day(), "days");
			const e = s.clone().add(6, "days");
			title = `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
		} else title = d.format("ddd, MMM D, YYYY");

		return (
			<div className="bo-cal">
				{this.renderSide(byDate)}
				<div className="bo-cal__main">
					<div className="bo-cal__top">
						<button className="bo-btn bo-btn--sm" type="button" onClick={() => this.setState({ calDate: today(), selDate: today(), pop: null })}>
							Today
						</button>
						<button className="bo-ico" type="button" aria-label="Previous" onClick={() => this.shift(-1)}>
							<I.chevL />
						</button>
						<button className="bo-ico" type="button" aria-label="Next" onClick={() => this.shift(1)}>
							<I.chevR />
						</button>
						<h2>{title}</h2>
						<span className="bo-spacer" />
						<div className="bo-legend">
							<span>
								<i style={{ background: "var(--bo-svc-monthend)" }} />
								Month-end
							</span>
							<span>
								<i style={{ background: "var(--bo-svc-payroll)" }} />
								Payroll
							</span>
							<span>
								<i style={{ background: "var(--bo-svc-ap)" }} />
								AP
							</span>
							<span>
								<i style={{ background: "var(--bo-svc-adhoc)" }} />
								Ad hoc
							</span>
							<span>
								<i className="bo-chk__sw bo-chk__sw--email" style={{ width: 9, height: 9 }} />
								Email send
							</span>
						</div>
						<div className="bo-seg">
							{["day", "week", "month"].map(m => (
								<button key={m} type="button" className={clsx({ "bo-seg__on": mode === m })} onClick={() => this.setMode(m)}>
									{m[0].toUpperCase() + m.slice(1)}
								</button>
							))}
						</div>
					</div>
					{mode === "month" ? this.renderMonth(byDate) : this.renderWeekOrDay(byDate)}
				</div>
				{this.renderPopover()}
			</div>
		);
	}
}

export default CalendarView;
